import { restoreAuthSession } from '@/api/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import type { RootState } from '@/redux/store';
import {
	usersGetSelfAccessGroupsStart,
	usersGetSelfStart,
} from '@/redux/users/usersSlice';
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import type {
	AccessGroupsDTO,
	AuthUser,
	GroupWithRolesDTO,
	RolesDTO,
	UserDatabasesDTO,
	UsersDTO,
} from '../redux/identity/identityTypes';
import {
	AUTH_TOKENS_PERSISTED_EVENT,
	getLoginUserFromCookie,
} from '../utils/authTokens';

export type AuthContextValue = {
	loginUser: AuthUser | null;
	selfUser: UsersDTO | null;
	isAuthenticated: boolean;
	isAuthInitializing: boolean;
	userRoles: string[];
	hasRole: (resource: string, action: string) => boolean;
	databases: { Id: string; Client: string }[] | null;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const SELF_USER_STORAGE_KEY = 'selfUser';
const ACCESS_GROUPS_STORAGE_KEY = 'accessGroups';
const USER_ROLES_STORAGE_KEY = 'userRoles';

const getDatabasesFromLoginUser = (loginUser: AuthUser | null) => {
	return (
		loginUser?.databases.map((d) => {
			const item = d.split(':');
			return {
				Id: item[0],
				Client: item[1],
			};
		}) || []
	);
};

const getDatabasesFromSelfUser = (
	getDatabasesFromSelfUser: UsersDTO | null,
): { Id: string; Client: string }[] | null => {
	if (
		!Array.isArray(getDatabasesFromSelfUser?.Databases) ||
		getDatabasesFromSelfUser.Databases.length === 0
	) {
		return null;
	}

	return getDatabasesFromSelfUser.Databases.map(
		(database: UserDatabasesDTO) => {
			return {
				Id: database.Id?.toString() || '',
				Client: database.Client || '',
			};
		},
	);
};

const getUserRolesFromAccessGroups = (
	accessGroups: (GroupWithRolesDTO | AccessGroupsDTO)[] | null | undefined,
): string[] => {
	if (!Array.isArray(accessGroups)) {
		return [];
	}

	const rolesSet = new Set<string>();

	accessGroups.forEach((group) => {
		if (!('Roles' in group) || !Array.isArray(group.Roles)) {
			return;
		}

		group.Roles.forEach((role: RolesDTO | null | undefined) => {
			if (!role?.Name || !role?.Action) {
				return;
			}

			const roleKey = `${role.Name}.${role.Action}`.toLowerCase();
			rolesSet.add(roleKey);
		});
	});

	return Array.from(rolesSet);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const dispatch = useAppDispatch();
	const { user, accessGroups, success } = useAppSelector(
		(state: RootState) => state.users,
	);

	const [loginUser, setLoginUser] = useState<AuthUser | null>(() =>
		getLoginUserFromCookie(),
	);
	const [isAuthInitializing, setIsAuthInitializing] = useState(
		() => !loginUser,
	);
	const [selfUser, setSelfUser] = useState<UsersDTO | null>(() => {
		const stored = localStorage.getItem(SELF_USER_STORAGE_KEY);
		if (!stored) {
			return null;
		}
		try {
			return JSON.parse(stored);
		} catch {
			return null;
		}
	});
	const [userRoles, setUserRoles] = useState<string[]>(() => {
		const storedUserRoles = localStorage.getItem(USER_ROLES_STORAGE_KEY);
		if (!storedUserRoles) {
			return [];
		}

		try {
			const parsedUserRoles = JSON.parse(storedUserRoles);

			if (
				Array.isArray(parsedUserRoles) &&
				parsedUserRoles.every((value) => typeof value === 'string')
			) {
				return parsedUserRoles;
			}

			return [];
		} catch {
			return [];
		}
	});

	const hasRole = useCallback(
		(resource: string, action: string) => {
			if (!resource || !action) {
				return false;
			}

			if (!Array.isArray(userRoles) || userRoles.length === 0) {
				return false;
			}

			const requiredKey = `${resource}.${action}`.toLowerCase();

			return userRoles.some((role) => role.toLowerCase() === requiredKey);
		},
		[userRoles],
	);

	// Tracks the current userId for the persist-event handler below — the
	// handler is registered once, so it cannot read loginUser state directly.
	const loginUserIdRef = useRef<string | null>(loginUser?.userId ?? null);
	useEffect(() => {
		loginUserIdRef.current = loginUser?.userId ?? null;
	}, [loginUser]);

	useEffect(() => {
		const handleAuthTokensPersisted = () => {
			const nextLoginUser = getLoginUserFromCookie();
			if (!nextLoginUser) {
				setLoginUser(null);
				return;
			}
			if (loginUserIdRef.current === nextLoginUser.userId) {
				// Silent token refresh for the same user (fires on any request
				// once the access token nears expiry). Keep cached roles — wiping
				// them blanks every role-gated control until the access-groups
				// refetch lands, or permanently if that refetch fails.
				return;
			}
			setSelfUser(null);
			localStorage.removeItem(SELF_USER_STORAGE_KEY);
			localStorage.removeItem(ACCESS_GROUPS_STORAGE_KEY);
			localStorage.removeItem(USER_ROLES_STORAGE_KEY);
			setUserRoles([]);
			setLoginUser(nextLoginUser);
		};

		window.addEventListener(
			AUTH_TOKENS_PERSISTED_EVENT,
			handleAuthTokensPersisted,
		);
		return () => {
			window.removeEventListener(
				AUTH_TOKENS_PERSISTED_EVENT,
				handleAuthTokensPersisted,
			);
		};
	}, []);

	useEffect(() => {
		if (!isAuthInitializing) {
			return undefined;
		}

		let active = true;
		const initializeAuth = async () => {
			const restored = await restoreAuthSession();
			if (!active) {
				return;
			}

			setLoginUser(restored ? getLoginUserFromCookie() : null);
			setIsAuthInitializing(false);
		};

		void initializeAuth();
		return () => {
			active = false;
		};
	}, [isAuthInitializing]);

	useEffect(() => {
		if (isAuthInitializing) {
			return;
		}
		if (!loginUser) {
			setSelfUser(null);
			localStorage.removeItem(SELF_USER_STORAGE_KEY);
			localStorage.removeItem(ACCESS_GROUPS_STORAGE_KEY);
			setUserRoles([]);
			localStorage.removeItem(USER_ROLES_STORAGE_KEY);
			return;
		}

		if (selfUser) {
			return;
		}
		dispatch(usersGetSelfStart({}));
	}, [dispatch, isAuthInitializing, loginUser, selfUser]);

	useEffect(() => {
		if (!loginUser) {
			return;
		}

		const storedAccessGroups = localStorage.getItem(ACCESS_GROUPS_STORAGE_KEY);

		if (storedAccessGroups) {
			try {
				const parsedAccessGroups = JSON.parse(storedAccessGroups) as (
					| GroupWithRolesDTO
					| AccessGroupsDTO
				)[];

				const parsedUserRoles =
					getUserRolesFromAccessGroups(parsedAccessGroups);
				setUserRoles(parsedUserRoles);
				localStorage.setItem(
					USER_ROLES_STORAGE_KEY,
					JSON.stringify(parsedUserRoles),
				);

				return;
			} catch {
				localStorage.removeItem(ACCESS_GROUPS_STORAGE_KEY);
			}
		}

		dispatch(usersGetSelfAccessGroupsStart());
	}, [dispatch, loginUser]);

	useEffect(() => {
		if (!success?.usersGetSelf || !user) {
			return;
		}

		setSelfUser(user);
		localStorage.setItem(SELF_USER_STORAGE_KEY, JSON.stringify(user));
	}, [success?.usersGetSelf, user]);

	useEffect(() => {
		if (!success?.usersGetSelfAccessGroups || !accessGroups) {
			return;
		}

		localStorage.setItem(
			ACCESS_GROUPS_STORAGE_KEY,
			JSON.stringify(accessGroups),
		);

		const parsedUserRoles = getUserRolesFromAccessGroups(accessGroups);
		setUserRoles(parsedUserRoles);
		localStorage.setItem(
			USER_ROLES_STORAGE_KEY,
			JSON.stringify(parsedUserRoles),
		);
	}, [accessGroups, success?.usersGetSelfAccessGroups]);

	const value = useMemo(
		() => ({
			loginUser,
			selfUser,
			isAuthenticated: Boolean(loginUser),
			isAuthInitializing,
			userRoles,
			hasRole,
			databases:
				getDatabasesFromSelfUser(selfUser) ||
				getDatabasesFromLoginUser(loginUser),
		}),
		[isAuthInitializing, loginUser, selfUser, userRoles, hasRole],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuthContext must be used within an AuthProvider');
	}
	return context;
};
